import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useParams } from 'react-router';

import { apiErrorMessage } from '../../shared/api/errors';
import { formatBytes, formatDate, formatDateTime } from '../../shared/lib/format';
import { Card, Spinner } from '../../shared/ui/primitives';
import { Logo } from '../../shared/ui/Logo';
import { listSoftware, softwareQueryKey } from '../software/api';
import { SoftwareList } from '../software/SoftwareList';
import { getPublicShare, publicShareDownloadUrl } from './api';

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function filesLabel(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return `${n} файл`;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return `${n} файла`;
  return `${n} файлов`;
}

/** Страница для получателя письма без аккаунта: только скачивание архива обследования. */
export function PublicSharePage() {
  const { token = '' } = useParams();

  const query = useQuery({
    queryKey: ['public-share', token],
    queryFn: () => getPublicShare(token),
    enabled: Boolean(token),
    retry: (count, err) =>
      !(err instanceof AxiosError && [404, 410].includes(err.response?.status ?? 0)) && count < 2,
  });
  const software = useQuery({ queryKey: softwareQueryKey, queryFn: listSoftware });

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center">
          <Logo size="lg" className="flex-col gap-2" />
        </div>

        <Card className="space-y-4 p-6">
          {query.isLoading && <Spinner />}

          {query.isError && (
            <div className="space-y-2 text-center">
              <p className="text-base font-medium text-ink">Ссылка недоступна</p>
              <p className="text-sm text-muted">
                {capitalize(apiErrorMessage(query.error, 'Не удалось открыть ссылку'))}. Попросите
                отправителя прислать новую.
              </p>
            </div>
          )}

          {query.data && (
            <>
              <div className="space-y-1">
                <h1 className="text-lg font-semibold text-ink">
                  Обследование от {formatDate(query.data.examinedAt)}
                  {query.data.modality && (
                    <span className="ml-2 text-base font-normal text-muted">
                      {query.data.modality}
                    </span>
                  )}
                </h1>
                {query.data.patientName && (
                  <p className="text-sm text-ink">Пациент: {query.data.patientName}</p>
                )}
                {query.data.senderName && (
                  <p className="text-sm text-muted">Отправитель: {query.data.senderName}</p>
                )}
                <p className="text-sm text-muted">
                  {filesLabel(query.data.fileCount)}, {formatBytes(query.data.totalSize)}
                </p>
              </div>

              <a
                href={publicShareDownloadUrl(token)}
                className="inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-600"
              >
                Скачать архив
              </a>

              <p className="text-xs text-muted">
                Ссылка действует до {formatDateTime(query.data.expiresAt)}. Архив содержит
                медицинские данные — не передавайте ссылку третьим лицам.
              </p>
            </>
          )}
        </Card>

        {query.data && (software.data?.length ?? 0) > 0 && (
          <Card className="space-y-3 p-6">
            <h2 className="text-sm font-semibold text-ink">Программа для просмотра снимков</h2>
            <SoftwareList />
          </Card>
        )}
      </div>
    </div>
  );
}
